import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession, useUser } from '@clerk/react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { isRunningLocal, SessionType } from '@shared';
import { selectSession } from '@client/model/selectors';
import { useSignOut } from '@client/components/hooks/use-sign-out';
import { FullScreenLoadingOverlay } from '@client/components/FullScreenLoadingOverlay';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@design-system';
import { Button } from '@/components/ui/button';
import {
  announceCachedNylIdlePageLoad,
  clearCachedNylIdleTimeoutPolicies,
  clearIdleSignOutTimeout,
  getIdleSessionEndedPath,
  getIdleSignOutFallbackPath,
  getIdleTimeoutPolicyStorageKey,
  getNylIdleTimeoutMs,
  getNylIdleWarningMs,
  formatIdleWarningCountdown,
  isIdleActivityExpired,
  isIdleSessionEndedPath,
  isNylIdleLogoutSession,
  NYL_IDLE_WARNING_MINUTES,
  readCachedNylIdleTimeoutPolicy,
  resolveNylIdleTimeoutPolicy,
  storeCachedNylIdleTimeoutPolicy,
  storeIdleSignInEmail,
  storeIdleSignOutTimeout,
  type CachedNylIdleTimeoutPolicy,
} from './constants';
import { useIdleLogout } from './use-idle-logout';

function useAnnounceCachedNylIdlePageLoad(activityScopeId: string | null) {
  const announcedActivityScopeId = useRef<string | null>(null);

  useEffect(() => {
    if (activityScopeId === null || announcedActivityScopeId.current === activityScopeId) return;

    announcedActivityScopeId.current = activityScopeId;
    announceCachedNylIdlePageLoad(activityScopeId);
  }, [activityScopeId]);
}

function usePersistNylIdleTimeoutPolicy({
  activityScopeId,
  hasConfirmedAppConfigPolicy,
  isEligibleSession,
  isEnabled,
  isSessionResolved,
  shouldClearAllCachedIdleState,
  timeoutMinutes,
}: {
  activityScopeId: string | null;
  hasConfirmedAppConfigPolicy: boolean;
  isEligibleSession: boolean;
  isEnabled: boolean;
  isSessionResolved: boolean;
  shouldClearAllCachedIdleState: boolean;
  timeoutMinutes: number;
}) {
  useEffect(() => {
    if (!isSessionResolved || activityScopeId === null) return;

    if (shouldClearAllCachedIdleState) {
      clearCachedNylIdleTimeoutPolicies();
      clearIdleSignOutTimeout();
      return;
    }

    if (!hasConfirmedAppConfigPolicy) return;

    storeCachedNylIdleTimeoutPolicy(activityScopeId, {
      isEnabled: isEligibleSession && isEnabled,
      timeoutMinutes,
    });
  }, [
    activityScopeId,
    hasConfirmedAppConfigPolicy,
    isEligibleSession,
    isEnabled,
    isSessionResolved,
    shouldClearAllCachedIdleState,
    timeoutMinutes,
  ]);
}

function useCrossTabNylIdleTimeoutPolicy(
  activityScopeId: string | null,
): CachedNylIdleTimeoutPolicy | null | undefined {
  const [policyUpdate, setPolicyUpdate] = useState<{
    activityScopeId: string;
    policy: CachedNylIdleTimeoutPolicy | null;
  } | null>(null);

  useEffect(() => {
    setPolicyUpdate(null);
    if (activityScopeId === null) return;

    const storageKey = getIdleTimeoutPolicyStorageKey(activityScopeId);
    const handlePolicyStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;

      setPolicyUpdate({
        activityScopeId,
        policy: event.newValue === null ? null : readCachedNylIdleTimeoutPolicy(activityScopeId),
      });
    };

    window.addEventListener('storage', handlePolicyStorage);
    return () => window.removeEventListener('storage', handlePolicyStorage);
  }, [activityScopeId]);

  return policyUpdate?.activityScopeId === activityScopeId ? policyUpdate.policy : undefined;
}

export function NylIdleLogoutGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const appSession = useSelector(selectSession);
  const { session: clerkSession, isLoaded: isClerkSessionLoaded } = useSession();
  const { user: clerkUser, isLoaded: isClerkUserLoaded } = useUser();
  const {
    flags: appConfigFlags,
    isError: didAppConfigFlagsFail,
    isLoaded: areAppConfigFlagsLoaded,
  } = useFeatureFlags();
  const activityScopeId = clerkUser?.id ?? null;
  useAnnounceCachedNylIdlePageLoad(activityScopeId);
  const currentIdleTimeoutPolicy = resolveNylIdleTimeoutPolicy(appConfigFlags);
  const crossTabIdleTimeoutPolicy = useCrossTabNylIdleTimeoutPolicy(activityScopeId);
  const cachedIdleTimeoutPolicy = activityScopeId
    ? readCachedNylIdleTimeoutPolicy(activityScopeId)
    : null;
  const hasConfirmedAppConfigPolicy = areAppConfigFlagsLoaded && !didAppConfigFlagsFail;
  const effectiveIdleTimeoutPolicy =
    crossTabIdleTimeoutPolicy !== undefined
      ? crossTabIdleTimeoutPolicy
      : hasConfirmedAppConfigPolicy
        ? currentIdleTimeoutPolicy
        : cachedIdleTimeoutPolicy;
  const timeoutMinutes =
    effectiveIdleTimeoutPolicy?.timeoutMinutes ?? currentIdleTimeoutPolicy.timeoutMinutes;
  const timeoutMs = getNylIdleTimeoutMs(timeoutMinutes);
  const signInEmail =
    clerkUser?.primaryEmailAddress?.emailAddress ?? appSession.advisor?.advisorEmail;
  const idleSessionEndedPath = getIdleSessionEndedPath(timeoutMinutes);
  const signOut = useSignOut(idleSessionEndedPath);
  const isSessionResolved = !appSession.isLoading && isClerkSessionLoaded && isClerkUserLoaded;
  const hasAuthenticatedClerkSession =
    isSessionResolved && Boolean(clerkSession?.id) && activityScopeId !== null;
  const isEligibleSession =
    hasAuthenticatedClerkSession &&
    isNylIdleLogoutSession(appSession, isRunningLocal() ? 'local-wizard-testing' : 'standard');
  const shouldClearAllCachedIdleState =
    hasAuthenticatedClerkSession &&
    appSession.sessionType === SessionType.advisor &&
    (!isEligibleSession || (hasConfirmedAppConfigPolicy && !currentIdleTimeoutPolicy.isEnabled));
  const isWaitingForPolicy =
    isEligibleSession &&
    !areAppConfigFlagsLoaded &&
    cachedIdleTimeoutPolicy?.isEnabled === true;
  const hasExpiredStoredActivity =
    isEligibleSession &&
    effectiveIdleTimeoutPolicy?.isEnabled === true &&
    activityScopeId !== null &&
    isIdleActivityExpired(activityScopeId, timeoutMs);
  const isEnabled =
    isEligibleSession &&
    effectiveIdleTimeoutPolicy?.isEnabled === true &&
    (areAppConfigFlagsLoaded || hasExpiredStoredActivity);

  usePersistNylIdleTimeoutPolicy({
    activityScopeId,
    hasConfirmedAppConfigPolicy,
    isEligibleSession,
    isEnabled: currentIdleTimeoutPolicy.isEnabled,
    isSessionResolved,
    shouldClearAllCachedIdleState,
    timeoutMinutes: currentIdleTimeoutPolicy.timeoutMinutes,
  });

  const handleIdleLogout = useCallback(async () => {
    storeIdleSignInEmail(signInEmail);
    if (activityScopeId !== null) storeIdleSignOutTimeout(activityScopeId, timeoutMinutes);
    navigate(idleSessionEndedPath, { replace: true });
    try {
      await signOut();
    } catch {
      window.location.assign(getIdleSignOutFallbackPath(timeoutMinutes));
    }
  }, [activityScopeId, idleSessionEndedPath, navigate, signInEmail, signOut, timeoutMinutes]);

  const { isWarningOpen, isLogoutPending, warningRemainingSeconds, staySignedIn, signOutNow } =
    useIdleLogout({
      enabled: isEnabled,
      activityScopeId,
      countPageLoadAsActivity: hasConfirmedAppConfigPolicy,
      timeoutMs,
      warningMs: getNylIdleWarningMs(timeoutMinutes),
      onLogout: handleIdleLogout,
    });
  const warningCountdown = formatIdleWarningCountdown(
    warningRemainingSeconds ?? NYL_IDLE_WARNING_MINUTES * 60,
  );

  const isIdleSessionEndedRoute = isIdleSessionEndedPath(location.pathname);
  if (
    !isIdleSessionEndedRoute &&
    (isWaitingForPolicy || hasExpiredStoredActivity || isLogoutPending)
  ) {
    return <FullScreenLoadingOverlay isLoading />;
  }

  return (
    <AlertDialog open={isWarningOpen}>
      <AlertDialogContent className="gap-4 rounded-[10px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Your session is about to expire</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground text-sm leading-5">
            For your security, you will be signed out in{' '}
            <span className="text-foreground font-semibold tabular-nums">{warningCountdown}</span>{' '}
            due to inactivity.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={signOutNow}
          >
            Sign out now
          </Button>
          <Button
            autoFocus
            onClick={staySignedIn}
          >
            Stay signed in
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
